import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Flag, XCircle, Trash, Warning, UserMinus, ShieldWarning } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin → Moderation tab. Lists reports against content/users and lets the
 * admin take action (dismiss / remove content / warn / ban).
 *
 * `onActionTaken` is invoked after any successful action so the parent
 * AdminPage can refresh top-level stats (the sidebar badges).
 */
const ModerationTab = ({ onActionTaken }) => {
  const [reports, setReports] = useState([]);
  const [modStats, setModStats] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [actioningReport, setActioningReport] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  const fetchReports = useCallback(async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await axios.get(`${API}/api/admin/moderation/queue${params}`, { withCredentials: true });
      setReports(res.data.items);
    } catch { /* silent */ }
  }, [filter]);

  const fetchModStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/moderation/stats`, { withCredentials: true });
      setModStats(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchReports(); fetchModStats(); }, [fetchReports, fetchModStats]);

  const handleAction = async (reportId, action) => {
    setActioningReport(reportId);
    try {
      await axios.post(
        `${API}/api/admin/moderation/${reportId}/action`,
        { action, admin_notes: adminNotes },
        { withCredentials: true },
      );
      const labels = { dismiss: 'Report dismissed', remove_content: 'Content removed', warn_user: 'User warned', ban_user: 'User banned' };
      toast.success(labels[action] || 'Action taken');
      setAdminNotes('');
      fetchReports();
      fetchModStats();
      if (onActionTaken) onActionTaken();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to take action');
    } finally {
      setActioningReport(null);
    }
  };

  return (
    <div data-testid="moderation-tab-content">
      {/* Moderation stats */}
      {modStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="glass-light rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-amber-400">{modStats.pending}</div>
            <div className="text-sm text-slate-400">Pending</div>
          </div>
          <div className="glass-light rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-blue-400">{modStats.under_review}</div>
            <div className="text-sm text-slate-400">Under Review</div>
          </div>
          <div className="glass-light rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-green-400">{modStats.resolved}</div>
            <div className="text-sm text-slate-400">Resolved</div>
          </div>
          <div className="glass-light rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-red-400">{modStats.banned_users}</div>
            <div className="text-sm text-slate-400">Banned Users</div>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2 mb-6" data-testid="moderation-filters">
        {['pending', 'under_review', 'resolved', 'dismissed', 'all'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              filter === f ? 'bg-amber-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
            data-testid={`mod-filter-${f}`}
          >
            {f === 'under_review' ? 'Under Review' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Reports list */}
      {reports.length === 0 ? (
        <div className="glass-light rounded-2xl p-12 text-center">
          <ShieldWarning className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500">No {filter !== 'all' ? filter : ''} reports</p>
          <p className="text-slate-600 text-sm">The moderation queue is clear</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reports.map(report => (
            <div key={report.id} className="glass-light rounded-2xl p-5" data-testid={`report-${report.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                    report.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                    report.status === 'under_review' ? 'bg-blue-500/20 text-blue-400' :
                    report.status === 'resolved' ? 'bg-green-500/20 text-green-400' :
                    'bg-slate-700 text-slate-400'
                  }`}>
                    {report.status === 'under_review' ? 'Under Review' : report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                  </span>
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                    report.target_type === 'content' ? 'bg-indigo-500/20 text-indigo-400' : 'bg-blue-500/20 text-blue-400'
                  }`}>
                    {report.target_type === 'content' ? 'Content' : 'User'}
                  </span>
                  <span className="text-sm text-slate-300">
                    {report.reason.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </span>
                </div>
                <span className="text-xs text-slate-600">
                  {new Date(report.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>

              <div className="bg-slate-800/50 rounded-xl p-3 mb-3">
                <div className="flex items-center gap-2 mb-1">
                  <Flag className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-400">
                    {report.target_type === 'content'
                      ? `Content: "${report.target_info?.title || 'Unknown'}"`
                      : `User: ${report.target_info?.display_name || report.target_info?.email || 'Unknown'}`}
                  </span>
                </div>
                <p className="text-xs text-slate-500">
                  Reported by: {report.reporter_name} | ID: {report.target_id.slice(0, 8)}...
                </p>
                {report.description && (
                  <p className="text-sm text-slate-300 mt-2 italic">&quot;{report.description}&quot;</p>
                )}
              </div>

              {report.admin_action && (
                <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-3 mb-3">
                  <p className="text-sm text-green-400">
                    Action: <strong>{report.admin_action.replace(/_/g, ' ')}</strong>
                  </p>
                  {report.admin_notes && (
                    <p className="text-xs text-slate-400 mt-1">Notes: {report.admin_notes}</p>
                  )}
                </div>
              )}

              {(report.status === 'pending' || report.status === 'under_review') && (
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Admin notes (optional)..."
                    value={actioningReport === report.id ? adminNotes : ''}
                    onChange={(e) => { setActioningReport(report.id); setAdminNotes(e.target.value); }}
                    onFocus={() => setActioningReport(report.id)}
                    className="w-full bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600"
                    data-testid={`admin-notes-${report.id}`}
                  />
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => handleAction(report.id, 'dismiss')}
                      className="px-3 py-2 bg-slate-800 text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-700 transition-colors flex items-center gap-1.5"
                      data-testid={`dismiss-${report.id}`}
                    >
                      <XCircle className="w-4 h-4" /> Dismiss
                    </button>
                    {report.target_type === 'content' && (
                      <button
                        onClick={() => {
                          if (window.confirm('Remove this content permanently?')) {
                            handleAction(report.id, 'remove_content');
                          }
                        }}
                        className="px-3 py-2 bg-red-500/10 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/20 transition-colors flex items-center gap-1.5"
                        data-testid={`remove-content-${report.id}`}
                      >
                        <Trash className="w-4 h-4" /> Remove Content
                      </button>
                    )}
                    <button
                      onClick={() => handleAction(report.id, 'warn_user')}
                      className="px-3 py-2 bg-amber-500/10 text-amber-400 rounded-lg text-sm font-medium hover:bg-amber-500/20 transition-colors flex items-center gap-1.5"
                      data-testid={`warn-user-${report.id}`}
                    >
                      <Warning className="w-4 h-4" /> Warn User
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm('Ban this user? They will be unable to log in.')) {
                          handleAction(report.id, 'ban_user');
                        }
                      }}
                      className="px-3 py-2 bg-red-500/10 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/20 transition-colors flex items-center gap-1.5"
                      data-testid={`ban-user-${report.id}`}
                    >
                      <UserMinus className="w-4 h-4" /> Ban User
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ModerationTab;
